import datetimeDecorator from 'src/decorators/datetime.decorator';

import type { PlanningsOperationEntity } from 'src/entities/PlanningsOperation.entity';
import type { PlanningsOperationEntityWithStatsEntity } from 'src/entities/PlanningsOperationEntityWithStats.entity';
import type { LaneBerthReservationForPlan } from 'src/entities/ShipmentOrderLegRun.entity';

type OperationWithBerthFields = PlanningsOperationEntity | PlanningsOperationEntityWithStatsEntity;

function berthReservationsForAction(op: OperationWithBerthFields): LaneBerthReservationForPlan[] {
  if (op.action === '積') {
    return op.loading_berth_reservations ?? [];
  }
  return op.unloading_berth_reservations ?? [];
}

function isValidBerthWindow(
  r: LaneBerthReservationForPlan,
): r is LaneBerthReservationForPlan & { reserved_from: string; reserved_to: string } {
  return Boolean(r.reserved_from && r.reserved_to);
}

/**
 * 地点滞在区間 [intervalStart, intervalEnd] が、全オペのバース予約枠のいずれかと重なりがあるかを検査する。
 * 有効枠（reserved_from / reserved_to 両方あり）が全オペで1件もない場合は false。
 * 有効枠が1件以上あり、区間がどの枠とも重ならない場合に true（時間外）。
 */
export function isOutsideBerthReservationTime(
  operations: OperationWithBerthFields[],
  intervalStart: string,
  intervalEnd: string,
): boolean {
  const startMs = new Date(intervalStart).getTime();
  const endMs = new Date(intervalEnd).getTime();
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
    return false;
  }

  const allReservations = operations.flatMap((op) =>
    berthReservationsForAction(op).filter(isValidBerthWindow),
  );
  if (allReservations.length === 0) {
    return false;
  }

  const hasOverlap = allReservations.some((r) => {
    const fromMs = new Date(r.reserved_from).getTime();
    const toMs = new Date(r.reserved_to).getTime();
    if (Number.isNaN(fromMs) || Number.isNaN(toMs)) {
      return false;
    }
    return startMs <= toMs && endMs >= fromMs;
  });

  return !hasOverlap;
}

/**
 * 地点に紐づくオペの有効なバース予約枠を集約し、一覧表示用の文字列にする（重複枠は除く）。
 */
export function formatBerthReservationIntervalsForDisplay(
  operations: OperationWithBerthFields[],
): string {
  const seen = new Set<string>();
  const parts: string[] = [];

  operations.forEach((op) => {
    berthReservationsForAction(op).filter(isValidBerthWindow).forEach((r) => {
      const key = `${r.reserved_from}\t${r.reserved_to}`;
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      const fromLabel = datetimeDecorator.toHourMinutes(new Date(r.reserved_from));
      const toLabel = datetimeDecorator.toHourMinutes(new Date(r.reserved_to));
      parts.push(`${fromLabel} - ${toLabel}`);
    });
  });

  if (parts.length === 0) {
    return '';
  }

  return `バース予約: ${parts.join(' / ')}`;
}
